import { useGetExamCourseSettingsByExamCourseCodeQuery } from '@exam-center/graphql';
import { Loading, ErrorState } from '@shared/components';

import { ExamCourseSettingsReadonlyFields } from '../../components';
import {
  AVERAGE_SCORE_CONDITION_MAP,
  CERTIFICATE_TEMPLATE_VARIANT_MAP,
  EXAM_DURATION_TYPE_MAP,
  QUESTIONNAIRE_TEMPLATE_MAP,
} from '../../constants';
import { type TemplateValue } from '../../utils/certificate-templates';
import { FailCertificateTemplatePreviewer } from '../fail-certificate-template-previewer';
import { PassCertificateTemplatePreviewer } from '../pass-certificate-template-previewer';

interface ExamCourseSettingsReadonlyDetailsProps {
  examCourseCode: string;
}

export default function ExamCourseSettingsReadonlyDetails(
  props: Readonly<ExamCourseSettingsReadonlyDetailsProps>,
) {
  const { examCourseCode } = props;

  const {
    data,
    loading: isLoading,
    error,
  } = useGetExamCourseSettingsByExamCourseCodeQuery({
    variables: {
      getExamCourseByExamCourseCodeInput: {
        examCourseCode,
      },
    },
  });

  const examCourse = data?.ecGetExamCourseByExamCourseCode.examCourse;

  if (isLoading) return <Loading />;

  if (!examCourse || error) return <ErrorState />;

  const visibleFields = {
    condition: !!examCourse.settings.certificate,
    averageScoreCondition: examCourse.settings.certificate?.averageScoreCondition
      ? !!AVERAGE_SCORE_CONDITION_MAP[examCourse.settings.certificate?.averageScoreCondition]
      : false,
    singleSessionDuration:
      !!examCourse.settings?.examDuration?.type &&
      EXAM_DURATION_TYPE_MAP[examCourse.settings.examDuration?.type] === 'single-session',

    multipleSessionDuration:
      !!examCourse.settings?.examDuration?.type &&
      EXAM_DURATION_TYPE_MAP[examCourse.settings.examDuration?.type] === 'multiple-session',

    attachDocument: examCourse.settings.attachDocument !== null,
  };

  const passedTemplate = examCourse.settings.certificate?.passedTemplate
    ? CERTIFICATE_TEMPLATE_VARIANT_MAP[examCourse.settings.certificate?.passedTemplate]
    : ('' as TemplateValue);

  const failedTemplate = examCourse.settings.certificate?.failedTemplate
    ? CERTIFICATE_TEMPLATE_VARIANT_MAP[examCourse.settings.certificate?.failedTemplate]
    : ('' as TemplateValue);

  const values = {
    certificate: {
      shouldIssue: !!examCourse.settings.certificate,
      averageScoreCondition: examCourse.settings.certificate?.averageScoreCondition
        ? AVERAGE_SCORE_CONDITION_MAP[examCourse.settings.certificate?.averageScoreCondition]
        : undefined,
      delayedDays: examCourse.settings.certificate?.delayedDays ?? null,
      validityDurationYears: examCourse.settings.certificate?.validityDurationYears ?? null,
      passCertificate: {
        template: passedTemplate,
        preview: (
          <PassCertificateTemplatePreviewer
            examCourseType={examCourse.type}
            examCourseLanguage={examCourse.language}
            variant={passedTemplate as TemplateValue}
          />
        ),
      },
      failCertificate: {
        template: failedTemplate,
        preview: (
          <FailCertificateTemplatePreviewer
            examCourseType={examCourse.type}
            examCourseLanguage={examCourse.language}
            variant={failedTemplate as TemplateValue}
          />
        ),
      },
    },
    examDuration: {
      type: examCourse?.settings?.examDuration?.type
        ? EXAM_DURATION_TYPE_MAP[examCourse?.settings?.examDuration?.type]
        : undefined,
      sessions: examCourse?.settings?.examDuration?.sessions,
    },
    resultDisplay: {
      shouldDisplay: examCourse?.settings.resultDisplay.examResult,
      resultSend: {
        shouldDisplay: !!examCourse?.settings.resultDisplay.examResult,
        portal: !!examCourse?.settings.resultSend.portal,
      },
    },
    questionnaire: {
      enabled: !!examCourse?.settings.questionnaire.templateId,
      templateId: examCourse?.settings.questionnaire.templateId
        ? QUESTIONNAIRE_TEMPLATE_MAP[examCourse?.settings.questionnaire.templateId]
        : null,
    },
    attachDocument: {
      enabled: !!examCourse?.settings.attachDocument,
      fileName: examCourse?.settings.attachDocument?.fileName ?? '',
    },
  };

  return <ExamCourseSettingsReadonlyFields visibleFields={visibleFields} values={values} />;
}
